/**
 * F37 (Wave 2D) — review-moderation action dialog.
 *
 * One dialog for all five `AdminReviewModerationAction` values
 * (hide/remove/restore/edit/lock — D20). A SegmentedControl picks the action;
 * a mandatory reason Textarea is required for every action; when action='edit'
 * an extra body Textarea is shown (the new review body). Posts via the parent's
 * `onConfirm`, which calls `POST /admin/reviews/:id/moderate`.
 *
 * Composes F02 primitives only (Modal / SegmentedControl / FormField /
 * Textarea / Button / Banner).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, SegmentedControl, Textarea } from '@bravemove/ui-web';
import {
  ADMIN_REVIEW_MODERATION_ACTIONS,
  AdminModerateReviewInputSchema,
  REVIEW_BODY_MAX,
  type AdminModerateReviewInput,
  type AdminReviewModerationAction,
  type TrainerReviewAdmin,
} from '@bravemove/types';

export interface ModerateReviewDialogProps {
  readonly open: boolean;
  readonly review: TrainerReviewAdmin | null;
  readonly onConfirm: (input: AdminModerateReviewInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function ModerateReviewDialog({
  open,
  review,
  onConfirm,
  onClose,
  errorMessage,
}: ModerateReviewDialogProps) {
  const { t } = useI18n();

  const [action, setAction] = useState<AdminReviewModerationAction>('hide');
  const [reason, setReason] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setAction('hide');
    setReason('');
    setBody(review?.body ?? '');
  }, [open, review]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: AdminModerateReviewInput = useMemo(
    () => ({
      action,
      reason,
      ...(action === 'edit' ? { body: body.trim() ? body : null } : {}),
    }),
    [action, reason, body],
  );
  const valid = useMemo(() => AdminModerateReviewInputSchema.safeParse(payload).success, [payload]);

  const actionOptions = useMemo(
    () =>
      ADMIN_REVIEW_MODERATION_ACTIONS.map((a) => ({
        value: a,
        label: t(`recommendation:moderation.action.${a}`, { defaultValue: a }),
      })),
    [t],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onConfirm(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t('admin:recommendation.moderation.dialog_title', { defaultValue: 'Moderate review' })}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="moderate-cancel"
          >
            {t('admin:recommendation.catalog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={action === 'restore' ? 'primary' : 'destructive'}
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="moderate-confirm"
          >
            {t('admin:recommendation.moderation.confirm', { defaultValue: 'Apply' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="moderate-review-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}

        <Banner tone="warning">
          {t('admin:recommendation.moderation.audit_notice', {
            defaultValue: 'Audit-logged action — the reason is recorded against your admin id.',
          })}
        </Banner>

        <FormField
          label={t('admin:recommendation.moderation.action_label', { defaultValue: 'Action' })}
          required
        >
          <SegmentedControl
            value={action}
            onChange={(v) => setAction(v as AdminReviewModerationAction)}
            options={actionOptions}
            aria-label={t('admin:recommendation.moderation.action_label', {
              defaultValue: 'Action',
            })}
          />
        </FormField>

        {action === 'edit' ? (
          <FormField
            label={t('admin:recommendation.moderation.body_label', { defaultValue: 'New body' })}
            helper={t('admin:recommendation.moderation.body_helper', {
              defaultValue: 'Replaces the visible review text. Leave empty to clear it.',
            })}
          >
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={REVIEW_BODY_MAX}
              data-testid="moderate-body"
            />
          </FormField>
        ) : null}

        <FormField
          label={t('recommendation:moderation.reason_label', { defaultValue: 'Reason (required)' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={t('admin:recommendation.moderation.reason_placeholder', {
              defaultValue: 'Why are you taking this action?',
            })}
            data-testid="moderate-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
